import React from 'react'
import type { LotteryRound } from '../../services/lotteryService'
import LotteryResultSectionHeader from './LotteryResultSectionHeader'

type Props = {
  sectionTitle: string
  rounds: LotteryRound[]
  onRoundClick?: (round: LotteryRound) => void
}

/** จาก roundNumber เช่น LYK-20260205-024 → "24" (ตัด 0 ข้างหน้าออก), LYK-20260205-001 → "1" */
function formatYeekeeRoundLabel(roundNumber: string | undefined): string {
  if (!roundNumber) return ''
  const parts = roundNumber.split('-')
  const suffix = parts[parts.length - 1]
  if (/^\d+$/.test(suffix)) return String(parseInt(suffix, 10))
  return roundNumber
}

/** Section: จับยี่กี VIP — แยก style ตามสถานะรอบ (ตรง latte-result.html)
 * 1. รอบปัจจุบัน: ช่วงเวลาปัจจุบัน และ status = 'open' → bgg-2 jackpot (แดง)
 * 2. รอบอนาคต: อยู่ในอนาคต และ status = 'open' → bgc-7 (รอผล)
 * 3. รอบจบแล้ว: ผ่านเวลาแล้ว หรือ status !== 'open' → bgg-6 (ขาว/แสดงผล)
 */
const LotteryResultSectionYeekeeVip: React.FC<Props> = ({ sectionTitle, rounds, onRoundClick }) => {
  const now = Date.now()
  const sorted = [...rounds].sort(
    (a, b) => new Date(a.betStartTime).getTime() - new Date(b.betStartTime).getTime()
  )
  const firstRound = sorted[0]
  const drawLabel = firstRound
    ? new Date(firstRound.betStartTime).toLocaleDateString('th-TH', {
        timeZone: 'Asia/Bangkok',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : ''

  return (
    <div className="col-12 px-1 mb-4">
      <LotteryResultSectionHeader
        title={sectionTitle}
        iconUrl={firstRound?.huayTypeIcon || 'logo.png'}
        drawLabel={drawLabel}
      />
      <div className="wrap-box p-2 pt-0 bgc-14 bdr-5">
        <div className="row mx--1">
          {sorted.map((round, index) => {
            const betStart = new Date(round.betStartTime).getTime()
            const betEnd = new Date(round.betEndTime).getTime()
            const isOpen = round.status === 'open'

            /** 1. รอบปัจจุบัน: อยู่ในช่วงเวลา bet และ status = 'open' */
            const isCurrent = isOpen && now >= betStart && now <= betEnd
            /** 2. รอบอนาคต: ยังไม่ถึงเวลาเริ่ม และ status = 'open' */
            const isFuture = isOpen && now < betStart

            const res = round.yeekee?.result
            const threeUp = res?.threeDigitTop ?? ''
            const twoDown = res?.twoDigitDown ?? res?.twoDigitTop ?? ''

            const drawTime = new Date(round.betStartTime).toLocaleTimeString('th-TH', {
              timeZone: 'Asia/Bangkok',
              hour: '2-digit',
              minute: '2-digit',
            })
            const roundLabel = formatYeekeeRoundLabel(round.roundNumber) || String(index + 1)

            /** Class ตรง latte-result.html: ปัจจุบัน=bgg-2 jackpot, อนาคต=bgc-7, จบแล้ว=bgg-6 */
            const cardClass = isCurrent
              ? 'bgg-2 bdr-5 bx-sd p-2 text-center jackpot h-100'
              : isFuture
                ? 'bgc-7 bdr-5 bx-sd p-2 text-center h-100'
                : 'bgg-6 bdr-5 bx-sd p-2 text-center h-100'
            const labelClass =
              isCurrent || isFuture ? 'px-1 t-medium c-red' : 't-medium c-white text-center'
            const valueBoxClass = isCurrent
              ? 'px-1 bgg-1 c-white bdr-5 bx-sd p-1 text-center'
              : 'px-1 bg-white bdr-5 bx-sd p-1 text-center'

            const displayThreeUp = isFuture ? 'รอผล' : threeUp || 'รอผล'
            const displayTwoDown = isFuture ? 'รอผล' : twoDown || 'รอผล'
            /** กดเข้าไปดูผลได้เฉพาะรอบที่ปิดรับแล้ว (status !== 'open') */
            const isClickable = !!onRoundClick && round.status !== 'open'

            return (
              <div
                key={round._id}
                className="col-6 col-lg-3 px-1 mt-2 animateMe"
                data-animation="fadeInUp"
              >
                <div
                  className={cardClass}
                  style={isClickable ? { cursor: 'pointer' } : undefined}
                  onClick={() => isClickable && onRoundClick?.(round)}
                  onKeyDown={(e) => isClickable && e.key === 'Enter' && onRoundClick?.(round)}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                >
                  <div className="row mx--2">
                    <div className="col-12 px--1 mb-1">
                      <div>
                        <div className={labelClass}>
                          <small>จับยี่กี VIP - รอบที่ {roundLabel}</small>
                        </div>
                        <div className={valueBoxClass}>
                          <h5>{drawTime} น.</h5>
                        </div>
                      </div>
                    </div>
                    <div className="col px--1">
                      <div>
                        <div className={labelClass}>
                          <small>3ตัวบน</small>
                        </div>
                        <div className={valueBoxClass}>
                          <h5>{displayThreeUp}</h5>
                        </div>
                      </div>
                    </div>
                    <div className="col px--1">
                      <div>
                        <div className={labelClass}>
                          <small>2ตัวล่าง</small>
                        </div>
                        <div className={valueBoxClass}>
                          <h5>{displayTwoDown}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default LotteryResultSectionYeekeeVip
